import React from 'react';
import planIcon from '../../../../../../assets/image/subscription/plan-icon.svg';
import Button from '../../../../../../component/ui/Button';
import { format2DigitDate } from '../../../../../../utils/constant';
import { axiosInstanceV2 } from '../../../../../../services/axios';
import { toast } from 'react-toastify';
import useSubscription from '../../../../../../hooks/useSubscription';
import {
	cancelSubscription,
	undoScheduledCancellation,
} from '../../../../../../services/subscription';

export default function CurrentPlanComponents({
	currentPlan,
	setIsOpenCancelSubscriptionModal,
	setSubscriptionScreenType,
}) {
	const { isSubscriptionFree, isSubscriptionActive, isSubscriptionRecurring, isPendingPayment } =
		useSubscription();

	const handleCancelSubscription = async (cancel_at_cycle_end = 1) => {
		try {
			await cancelSubscription({ cancel_at_cycle_end });
			toast.success('Switched to Free plan');
			window.location.reload();
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to switch to free plan');
		}
	};

	const getStatusStyle = (status) => {
		switch (status) {
			case 'Active':
				return { backgroundColor: '#F0FFE6', color: '#68C230' };
			case 'Paused':
				return { backgroundColor: '#F3EEFF', color: '#8B5CF6' };
			case 'Created':
			case 'Authenticated':
				return { backgroundColor: '#EEF6FF', color: '#0D6EFD' };
			case 'Pending':
				return { backgroundColor: '#FFF8E6', color: '#E6A700' };
			case 'Halted':
				return { backgroundColor: '#FFF4E5', color: '#E67E22' };
			case 'Cancelled':
			case 'Expired':
			case 'Superseded':
				return { backgroundColor: '#FDECEC', color: '#DC3545' };
			default:
				return { backgroundColor: '#F5F5F5', color: '#6C757D' };
		}
	};

	// Undoes a pending "cancel at cycle end" request. Nothing was ever told
	// to Razorpay for it, so this just removes the scheduled job — the
	// subscription keeps renewing normally, no payment/checkout involved.
	const handleResumeSubscription = async () => {
		try {
			const result = await undoScheduledCancellation();
			toast.success(result?.data?.message ?? 'Subscription will continue renewing normally.');
			window.location.reload();
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Failed to resume subscription');
		}
	};

	const handleCancelScheduledDowngrade = async () => {
		try {
			const token = localStorage.getItem('authToken');
			await axiosInstanceV2.post(
				'/subscription/cancel-scheduled-downgrade',
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);
			toast.success('Scheduled downgrade cancelled.');
			window.location.reload();
		} catch (error) {
			console.error('Failed to cancel scheduled downgrade:', error);
			toast.error(error?.response?.data?.message ?? 'Unable to cancel scheduled downgrade.');
		}
	};

	return (
		<div>
			<div className="fs-3 fw-semibold py-2">Current Subscription</div>
			{isPendingPayment && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#EEF6FF', border: '1px solid #6EA8FE' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>⏳</span>
						<div className="fw-semibold text-dark">Payment Pending</div>
					</div>
					<div className="mt-2 fs-5">
						Your subscription to <strong>{currentPlan?.plan?.name}</strong> was created but payment
						hasn't been completed yet.
					</div>
				</div>
			)}

			{currentPlan?.is_downgrade_scheduled && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#EEF6FF', border: '1px solid #6EA8FE' }}
				>
					<div className="d-flex align-items-center gap-4">
						<span style={{ fontSize: 20 }}>📅</span>
						<div className="fw-semibold">Downgrade Scheduled</div>
					</div>
					<div className="mt-2 fs-5">
						Your subscription will automatically change to{' '}
						<strong>{currentPlan?.downgrade_plan?.name}</strong> on{' '}
						<strong>{format2DigitDate(currentPlan?.downgrade_at)}</strong>.
					</div>
					<div className="mt-1 fs-5 text-muted">
						You'll continue enjoying your current plan until then.
					</div>
				</div>
			)}

			{currentPlan?.is_scheduled_for_cancellation && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#FFF4E5', border: '1px solid #F5B97F' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>⚠️</span>
						<div className="fw-semibold text-dark">Cancellation Scheduled</div>
					</div>
					<div className="mt-2 fs-5">
						Your subscription is scheduled to end on{' '}
						<strong>{format2DigitDate(currentPlan?.current_end)}</strong> and will switch to the
						Free plan. You'll keep full access until then — no charge will be made.
					</div>
					<div className="mt-1 fs-5 text-muted">
						Changed your mind? Resume your subscription any time before then to keep it running with
						no interruption.
					</div>
				</div>
			)}

			{isSubscriptionActive &&
				!isSubscriptionRecurring &&
				!isSubscriptionFree &&
				!currentPlan?.is_downgrade_scheduled &&
				!currentPlan?.is_scheduled_for_cancellation && (
					<div
						className="mb-3 rounded-3 p-3"
						style={{ background: '#F5F5F5', border: '1px solid #D9D9D9' }}
					>
						<div className="fs-5 text-muted">
							This is a one-time plan valid until{' '}
							<strong>{format2DigitDate(currentPlan?.current_end)}</strong>. It won't auto-renew —
							once it ends, your account moves to the Free plan automatically. You can subscribe
							again anytime.
						</div>
					</div>
				)}

			<div
				className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center rounded-3 p-2"
				style={{
					border: '1px solid rgba(85, 91, 103, 0.15)',
					backgroundColor: 'rgba(252, 252, 252, 1)',
				}}
			>
				<div className="d-flex justify-content-start align-items-center p-2">
					<img
						src={planIcon}
						className="px-2"
						alt="icon"
					/>
					<div className="px-3">
						<div className="d-flex gap-4 align-items-center">
							<div className="fs-4 font-medium">{`${currentPlan?.plan?.name || 'Free Plan'}`}</div>
							<div
								className="p-2 fs-5 font-small rounded-4"
								style={{ ...getStatusStyle(currentPlan?.status), width: '50px' }}
							>
								{currentPlan?.status}
							</div>
						</div>
						<div className="fs-5 font-small">
							Description: {currentPlan?.plan?.description || 'N/A'}
						</div>
						<div className="fs-5 font-small">
							{isSubscriptionFree
								? 'No payment subscription'
								: `Subscription Id : ${currentPlan?.razorpay_subscription_id}`}
						</div>
					</div>
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Amount'}</div>
					</div>
					{isSubscriptionFree ? (
						<>
							<div className="fs-5 font-small text-success">Free Forever</div>
							<div className="fs-5 font-small">No recurring charges</div>
						</>
					) : (
						<>
							<div className="fs-5 font-small">
								₹ {currentPlan?.amount} / {currentPlan?.billing_period}
							</div>
							<div className="fs-5 font-small">{`Billed ${currentPlan?.billing_period}`}</div>
						</>
					)}
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Validity'}</div>
					</div>
					<div className="fs-5 font-small">{`${currentPlan?.current_start !== null ? format2DigitDate(currentPlan?.current_start) : ''} - ${currentPlan?.current_end !== null ? format2DigitDate(currentPlan?.current_end) : ''}`}</div>
					{currentPlan?.is_downgrade_scheduled ? (
						<div className="fs-5 font-small text-primary">
							Downgrades on {format2DigitDate(currentPlan.downgrade_at)}
						</div>
					) : currentPlan?.next_billing_date ? (
						<div className="fs-5 font-small">
							Next billing on {format2DigitDate(currentPlan.next_billing_date)}
						</div>
					) : currentPlan?.is_scheduled_for_cancellation ? (
						<div className="fs-5 font-small text-warning">
							Subscription ends on {format2DigitDate(currentPlan.current_end)}
						</div>
					) : !isSubscriptionRecurring && !isSubscriptionFree ? (
						<div className="fs-5 font-small text-muted">
							Moves to Free on {format2DigitDate(currentPlan?.current_end)}
						</div>
					) : null}
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Status'}</div>
					</div>
					<div
						className="p-2 fs-5 font-small rounded-4"
						style={{
							backgroundColor: currentPlan?.is_downgrade_scheduled
								? '#EEF6FF'
								: currentPlan?.is_scheduled_for_cancellation
									? '#FFF4E5'
									: '#F0FFE6',
							color: currentPlan?.is_downgrade_scheduled
								? '#0D6EFD'
								: currentPlan?.is_scheduled_for_cancellation
									? '#E67E22'
									: '#68C230',
						}}
					>
						{currentPlan?.is_downgrade_scheduled
							? 'Downgrade Scheduled'
							: currentPlan?.is_scheduled_for_cancellation
								? 'Scheduled for Cancellation'
								: currentPlan?.status}
					</div>
					<div className="fs-5 font-small">
						{!isSubscriptionFree && isSubscriptionRecurring && (
							<div className="fs-5 font-small">
								Auto Renewal
								<span
									className={`ms-2 fw-semibold ${
										currentPlan?.auto_renewal ? 'text-success' : 'text-danger'
									}`}
								>
									{currentPlan?.auto_renewal ? 'ON' : 'OFF'}
								</span>
							</div>
						)}
					</div>
				</div>
				<div className="align-items-center justify-content-end px-3 py-2 ">
					{!isSubscriptionFree && (
						<Button
							type="button"
							className="mt-0 btn-primary fs-5 fw-light add-pro py-1 px-4"
							button="Manage Billing"
							onClick={() => setSubscriptionScreenType('manage-billing')}
						/>
					)}
					{currentPlan?.is_downgrade_scheduled && (
						<div>
							<div
								className="mt-3 fs-5 font-small text-warning cursor-pointer"
								onClick={handleCancelScheduledDowngrade}
							>
								Cancel Downgrade
							</div>
						</div>
					)}
					{!isSubscriptionFree &&
						isSubscriptionActive &&
						isSubscriptionRecurring &&
						!currentPlan?.is_downgrade_scheduled &&
						(currentPlan?.is_scheduled_for_cancellation ? (
							<div
								className="mt-3 fs-5 font-small text-primary cursor-pointer"
								onClick={handleResumeSubscription}
							>
								Resume Subscription
							</div>
						) : (
							<div
								className="mt-3 fs-5 font-small text-danger cursor-pointer"
								onClick={() => setIsOpenCancelSubscriptionModal(true)}
							>
								Cancel Subscription
							</div>
						))}
					{isPendingPayment && (
						<div className="mt-3 d-flex gap-3">
							<div
								className="fs-5 font-small text-danger cursor-pointer align-self-center"
								onClick={() => handleCancelSubscription(0)}
							>
								Switch to Previous Plan instead
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
